'use client';

export interface DayProgress {
  complete: boolean;
}

export interface WeekProgress {
  daysComplete: [boolean, boolean, boolean, boolean, boolean];
  assessmentPassed: boolean;
  startedAt: string | null;
  completedAt: string | null;
}

export interface Settings {
  theme: 'dark' | 'light' | 'system';
  accentColor: string;
  startDate: string | null;
  showCompleted: boolean;
}

export interface StreakData {
  lastActiveDate: string | null;
  currentStreak: number;
  longestStreak: number;
}

export interface Goals {
  weeklyTarget: number;
  currentWeekDaysLogged: number;
}

export interface StudyLogEntry {
  date: string;
  weekNum: number;
  dayIndex: number;
  minutesStudied: number;
}

export interface Resource {
  label: string;
  url: string;
}

// ── Generic localStorage helpers ──────────────────────────────────────────

function lsGet<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsSet<T>(key: string, value: T): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or unavailable — silently fail
  }
}

// ── Progress ──────────────────────────────────────────────────────────────

const PROGRESS_KEY = 'lms_progress';

export function getProgress(): Record<number, WeekProgress> {
  return lsGet<Record<number, WeekProgress>>(PROGRESS_KEY, {});
}

export function getWeekProgress(weekNum: number): WeekProgress {
  const all = getProgress();
  return all[weekNum] ?? {
    daysComplete: [false, false, false, false, false],
    assessmentPassed: false,
    startedAt: null,
    completedAt: null,
  };
}

export function setWeekProgress(weekNum: number, progress: WeekProgress): void {
  const all = getProgress();
  all[weekNum] = progress;
  lsSet(PROGRESS_KEY, all);
}

export function toggleDay(weekNum: number, dayIndex: number): WeekProgress {
  const p = getWeekProgress(weekNum);
  const days = [...p.daysComplete] as [boolean, boolean, boolean, boolean, boolean];
  days[dayIndex] = !days[dayIndex];
  const now = new Date().toISOString();
  const started = p.startedAt ?? (days.some(Boolean) ? now : null);
  const allDone = days.every(Boolean) && p.assessmentPassed;
  const updated: WeekProgress = {
    ...p,
    daysComplete: days,
    startedAt: started,
    completedAt: allDone ? now : null,
  };
  setWeekProgress(weekNum, updated);
  if (days[dayIndex]) {
    updateStreak();
    logStudyDay(weekNum, dayIndex);
  }
  return updated;
}

export function toggleAssessment(weekNum: number): WeekProgress {
  const p = getWeekProgress(weekNum);
  const now = new Date().toISOString();
  const allDaysDone = p.daysComplete.every(Boolean);
  const updated: WeekProgress = {
    ...p,
    assessmentPassed: !p.assessmentPassed,
    completedAt: !p.assessmentPassed && allDaysDone ? now : null,
  };
  setWeekProgress(weekNum, updated);
  return updated;
}

// ── Notes ─────────────────────────────────────────────────────────────────

const NOTES_KEY = 'lms_notes';

export function getNoteKey(weekNum: number, dayIndex: number): string {
  return `week-${weekNum}-day-${dayIndex}`;
}

export function getNote(weekNum: number, dayIndex: number): string {
  const all = lsGet<Record<string, string>>(NOTES_KEY, {});
  return all[getNoteKey(weekNum, dayIndex)] ?? '';
}

export function setNote(weekNum: number, dayIndex: number, text: string): void {
  const all = lsGet<Record<string, string>>(NOTES_KEY, {});
  all[getNoteKey(weekNum, dayIndex)] = text;
  lsSet(NOTES_KEY, all);
}

// ── Resources ─────────────────────────────────────────────────────────────

const RESOURCES_KEY = 'lms_resources';

export function getResources(weekNum: number): Resource[] {
  const all = lsGet<Record<number, Resource[]>>(RESOURCES_KEY, {});
  return all[weekNum] ?? [];
}

export function setResources(weekNum: number, resources: Resource[]): void {
  const all = lsGet<Record<number, Resource[]>>(RESOURCES_KEY, {});
  all[weekNum] = resources;
  lsSet(RESOURCES_KEY, all);
}

// ── Settings ──────────────────────────────────────────────────────────────

const SETTINGS_KEY = 'lms_settings';

const DEFAULT_SETTINGS: Settings = {
  theme: 'dark',
  accentColor: '#1D9E75',
  startDate: null,
  showCompleted: true,
};

export function getSettings(): Settings {
  return lsGet<Settings>(SETTINGS_KEY, DEFAULT_SETTINGS);
}

export function setSettings(settings: Settings): void {
  lsSet(SETTINGS_KEY, settings);
}

// ── Streak ────────────────────────────────────────────────────────────────

const STREAK_KEY = 'lms_streak';

const DEFAULT_STREAK: StreakData = {
  lastActiveDate: null,
  currentStreak: 0,
  longestStreak: 0,
};

export function getStreak(): StreakData {
  return lsGet<StreakData>(STREAK_KEY, DEFAULT_STREAK);
}

function updateStreak(): void {
  const streak = getStreak();
  const today = new Date().toISOString().split('T')[0];
  const last = streak.lastActiveDate;

  if (last === today) return; // already counted today

  let current = streak.currentStreak;
  if (last) {
    const lastDate = new Date(last);
    const todayDate = new Date(today);
    const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / 86400000);
    current = diffDays === 1 ? current + 1 : 1;
  } else {
    current = 1;
  }

  const longest = Math.max(current, streak.longestStreak);
  lsSet(STREAK_KEY, { lastActiveDate: today, currentStreak: current, longestStreak: longest });
}

// ── Goals ─────────────────────────────────────────────────────────────────

const GOALS_KEY = 'lms_goals';

const DEFAULT_GOALS: Goals = { weeklyTarget: 5, currentWeekDaysLogged: 0 };

export function getGoals(): Goals {
  return lsGet<Goals>(GOALS_KEY, DEFAULT_GOALS);
}

export function setGoals(goals: Goals): void {
  lsSet(GOALS_KEY, goals);
}

// ── Study Log ─────────────────────────────────────────────────────────────

const STUDY_LOG_KEY = 'lms_studyLog';

export function getStudyLog(): StudyLogEntry[] {
  return lsGet<StudyLogEntry[]>(STUDY_LOG_KEY, []);
}

function logStudyDay(weekNum: number, dayIndex: number): void {
  const log = getStudyLog();
  const today = new Date().toISOString().split('T')[0];
  const exists = log.some(e => e.date === today && e.weekNum === weekNum && e.dayIndex === dayIndex);
  if (!exists) {
    log.push({ date: today, weekNum, dayIndex, minutesStudied: 60 });
    lsSet(STUDY_LOG_KEY, log);
  }
}

// ── Derived Stats ─────────────────────────────────────────────────────────

export function computeStats() {
  const progress = getProgress();
  const weeks = Object.values(progress);

  const tasksComplete = weeks.reduce((sum, w) => sum + w.daysComplete.filter(Boolean).length, 0);
  const assessmentsPassed = weeks.filter(w => w.assessmentPassed).length;
  const weeksComplete = weeks.filter(w => w.daysComplete.every(Boolean) && w.assessmentPassed).length;
  const weeksInProgress = weeks.filter(w => w.daysComplete.some(Boolean) && !w.completedAt).length;

  const log = getStudyLog();
  const totalMinutes = log.reduce((sum, e) => sum + e.minutesStudied, 0);
  const studyHours = Math.round(totalMinutes / 60);

  const overallPct = Math.round((weeksComplete / 80) * 100);

  return {
    tasksComplete,
    assessmentsPassed,
    weeksComplete,
    weeksInProgress,
    studyHours,
    overallPct,
    totalTasksAvailable: 80 * 5,
  };
}

export function computePhaseProgress(): Record<number, { complete: number; total: number; pct: number }> {
  const progress = getProgress();
  const phaseTotals: Record<number, { complete: number; total: number }> = {
    1: { complete: 0, total: 20 },
    2: { complete: 0, total: 16 },
    3: { complete: 0, total: 16 },
    4: { complete: 0, total: 12 },
    5: { complete: 0, total: 8 },
    6: { complete: 0, total: 8 },
  };

  const phaseRanges: Record<number, [number, number]> = {
    1: [1, 20], 2: [21, 36], 3: [37, 52], 4: [53, 64], 5: [65, 72], 6: [73, 80],
  };

  for (const [phaseStr, range] of Object.entries(phaseRanges)) {
    const [start, end] = range as [number, number];
    const phase = Number(phaseStr);
    for (let w = start; w <= end; w++) {
      const wp = progress[w];
      if (wp?.daysComplete.every(Boolean) && wp.assessmentPassed) {
        phaseTotals[phase].complete++;
      }
    }
  }

  const result: Record<number, { complete: number; total: number; pct: number }> = {};
  for (const [p, data] of Object.entries(phaseTotals)) {
    const complete = data.complete;
    const total = data.total;
    result[Number(p)] = { complete, total, pct: Math.round((complete / total) * 100) };
  }
  return result;
}

export function getCurrentWeek(): number {
  const progress = getProgress();
  // Find the first week that's not fully complete
  for (let w = 1; w <= 80; w++) {
    const wp = progress[w];
    if (!wp || !wp.daysComplete.every(Boolean) || !wp.assessmentPassed) {
      return w;
    }
  }
  return 80;
}

// ── Export / Import ───────────────────────────────────────────────────────

export function exportAllData(): string {
  const data = {
    lms_progress: getProgress(),
    lms_notes: lsGet('lms_notes', {}),
    lms_resources: lsGet('lms_resources', {}),
    lms_settings: getSettings(),
    lms_streak: getStreak(),
    lms_goals: getGoals(),
    lms_studyLog: getStudyLog(),
    exportedAt: new Date().toISOString(),
  };
  return JSON.stringify(data, null, 2);
}

export function importAllData(json: string): void {
  const data = JSON.parse(json);
  const keys = ['lms_progress', 'lms_notes', 'lms_resources', 'lms_settings', 'lms_streak', 'lms_goals', 'lms_studyLog'];
  for (const key of keys) {
    if (data[key] !== undefined) {
      lsSet(key, data[key]);
    }
  }
}

export function resetAllProgress(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem('lms_progress');
    window.localStorage.removeItem('lms_notes');
    window.localStorage.removeItem('lms_resources');
    window.localStorage.removeItem('lms_streak');
    window.localStorage.removeItem('lms_goals');
    window.localStorage.removeItem('lms_studyLog');
  } catch {
    // ignore
  }
}